/**
 * Context-locked palette for the "Fire Bans & Restrictions" layer (#213, Fire-Risk
 * group). The Alberta Fire Ban System escalation — Advisory → Restriction → Ban →
 * Forest-Area Closure — plus Fire Control Orders, from the arcgis_public_features
 * `fire_restriction` category.
 *
 * This is a REGULATORY-RESTRICTION register, NOT an active-fire register: it answers
 * "what's prohibited here," not "where is it burning." So it deliberately owns a
 * muted BURNT-AMBER / charred-brown ramp that is luminance- and saturation-separated
 * from the bright active-fire hues — the OC-FUL flame (#e25822) and the urgent
 * anomaly red (#ff3b30, Colour Law 5). A fire ban is a darker, drier brown; nothing
 * in this family impersonates that red. Escalation reads via deepening brown + a
 * heavier outline (width, not motion — §3B.8 motion budget). Always paired with the
 * tier label at the call site.
 *
 * This module must NOT import another palette family (ESLint no-restricted-imports).
 */
import type { ArcgisLayerStyle } from '../layers/arcgisStyle';

const ADVISORY: ArcgisLayerStyle = {
  color: '#b45309', // amber-700 — burnt amber, the lightest restriction tier
  fillAlpha: 0.12,
  strokeAlpha: 0.85,
  outlineWidth: 2,
  pointPixelSize: 11,
  label: 'Fire Advisory',
};

const RESTRICTION: ArcgisLayerStyle = {
  color: '#9a3412', // orange-800 — rust
  fillAlpha: 0.14,
  strokeAlpha: 0.88,
  outlineWidth: 3,
  pointPixelSize: 12,
  label: 'Fire Restriction',
};

const BAN: ArcgisLayerStyle = {
  color: '#7c2d12', // orange-900 — dark rust-brown, mandatory prohibition
  fillAlpha: 0.16,
  strokeAlpha: 0.9,
  outlineWidth: 4,
  pointPixelSize: 13,
  label: 'Fire Ban',
};

const CLOSURE: ArcgisLayerStyle = {
  color: '#5b1f0e', // charred brown — the heaviest tier (area closed entirely)
  fillAlpha: 0.18,
  strokeAlpha: 0.92,
  outlineWidth: 4,
  pointPixelSize: 13,
  label: 'Forest Area Closure',
};

const UNKNOWN: ArcgisLayerStyle = {
  color: '#9ca3af', // neutral grey — an unmapped tier still renders, never dropped
  fillAlpha: 0.1,
  strokeAlpha: 0.7,
  outlineWidth: 2,
  pointPixelSize: 11,
  label: 'Fire restriction',
};

/**
 * Resolve the style for an Alberta fire-restriction `alert_type` (free-text from the
 * feed, e.g. "Fire Advisory", "Fire Restriction", "Fire Ban", "Forest Area Closure";
 * Fire Control Orders publish "Fire Restriction"). Matched case-insensitively by the
 * most-specific keyword first. null/unrecognised → neutral grey.
 */
export function fireRestrictionStyle(severity: string | null | undefined): ArcgisLayerStyle {
  const s = (severity ?? '').toLowerCase();
  if (s.includes('closure')) return CLOSURE;
  if (s.includes('ban')) return BAN;
  if (s.includes('restriction')) return RESTRICTION;
  if (s.includes('advisory')) return ADVISORY;
  return UNKNOWN;
}
